<template>
<Layout>
  <span :key="componentKey">
    <div class="step step-one" v-if="this.stepNum === 1" ref="steps">
      <h1 class="step-heading">Is there a diet you want to follow?</h1>
      <div class="diet-options">
        <BaseCheckboxListItem
          :items="dietPrefs"
          :showSelectAll="true"
          :allowToggleAll="false"
          :allOpt="selectAllOpt"
          :selectMultiple="false"
          :onTabletUp="el.is.tabletUp"
          :validateSelect="true"
          @is-valid="onValidate"
        />
      </div>
      <div class="btn-wrapper">
        <BaseButton type="button" class="btn-medium btn-step" v-if="this.isValid" @click="next(2)">
          Next
        </BaseButton>
      </div>
    </div>

    <div class="step step-two" v-if="this.stepNum === 2">
      <div class="search-wrapper">
        <h1 class="step-heading">Do you have any restrictions or health considerations?</h1>
        <span class="icon-clear-wrapper" @click="clearSearch" v-if="this.query !== ''">
          <BaseIcon class="icon-clear" name="times-circle" />
        </span>
        <baseInput inputType="text" placeholder="Search" v-model="query" />
        <BaseCheckboxTags class="checkbox-tags" name="health_options" :items="healthPrefs" />
      </div>
      <div class="btn-wrapper">
        <BaseButton type="button" class="btn-medium btn-step" @click="done">
          Done
        </BaseButton>
      </div>
    </div>
  </span>
</Layout>
</template>

<script>
import Layout from 'layouts/embed';
import { breakpointsMixin } from 'breakpointsMixin';
import { gtmMixin } from 'gtmMixin';
import { onboardingComputed, onboardingMethods, userComputed, userMethods } from '@state/helpers';

export default {
  mixins: [breakpointsMixin, gtmMixin],
  components: {
    Layout,
  },
  props: {
    user: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      componentKey: 0,
      isValid: false,
      stepNum: 1,
      query: '',
      selectAllOpt: {
        name: 'No Diet',
        subtitle: 'Show me everything',
        isSelected: false,
      },
      showTagsCount: 18,
    };
  },
  page() {
    return {
      title: 'Onboarding',
      meta: [
        {
          name: 'Onboarding',
          content: 'Onboarding',
        },
      ],
    };
  },
  computed: {
    ...onboardingComputed,
    ...userComputed,
    dietPrefs() {
      return this.preferences ? this.preferences.filter((pref) => pref.preferenceCategoryId === 1) : [];
    },
    healthPrefs() {
      if (this.preferences) {
        const healthPrefs = this.preferences.filter((pref) => pref.preferenceCategoryId === 3);

        if (this.query) {
          return healthPrefs.filter((pref) => pref.name.toLocaleLowerCase().includes(this.query.toLocaleLowerCase()));
        } else {
          return healthPrefs.slice(0, this.showTagsCount);
        }
      } else {
        return [];
      }
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    ...onboardingMethods,
    ...userMethods,
    init() {
      this.setIsOnboarding(true);
      this.getPreferences();
      this.calcVisibleHealthTags();
    },
    calcVisibleHealthTags() {
      if (this.$refs.steps) {
        const stepsHeight = this.$refs.steps.clientHeight;

        if (stepsHeight <= 568) {
          this.showTagsCount = 7;
        } else if (stepsHeight <= 667) {
          this.showTagsCount = 12;
        } else if (stepsHeight <= 667) {
          this.showTagsCount = 14;
        }
      }
    },
    async next(num) {
      this.stepNum = num;
    },
    async done() {
      await this.updatePreferences({ preferences: this.preferences });
      this.$router.push({ name: 'embed' });
    },
    clearSearch() {
      if (this.query !== '') {
        this.query = '';
      }
    },
    onValidate(evt) {
      this.isValid = evt;
    },
  },
  watch: {
    retry(newVal, preVal) {
      this.init();
      this.componentKey += 1;
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'theme.scss';

.step {
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  overflow-y: scroll;
  padding: 3rem 2rem 4rem;
  text-align: center;

  // iPhone 6 up
  @include media('md', null) {
    padding-top: 5rem;
  }

  h1,
  h2,
  p {
    color: white;
  }

  .icon-clipbaord {
    display: flex;
    justify-content: center;
    margin-bottom: 2rem;
    width: 15%;

    // iPhone 6 up
    @include media('md', null) {
      width: 100%;
    }
  }

  .step-heading {
    font-size: 2.5rem;
    line-height: 1.2;
    margin-bottom: 3rem;
  }

  .step-text {
    font-size: 1.8rem;
    line-height: 1;
    margin-bottom: 1.5rem;

    // iPhone 6 up
    @include media('md', null) {
      font-size: 2rem;
    }
  }

  .step-notice {
    background-color: white;
    border-radius: $base-border-radius;
    margin-bottom: 1.5rem;
    padding: 1rem 2rem;
    text-align: center;

    // iPhone 6 up
    @include media('md', null) {
      padding: 2rem 4rem;
    }

    .icon-warning-shield {
      width: 20%;
    }

    p {
      color: $color-text;
      font-size: 1.8rem;
      line-height: 1;
      margin-top: 1rem;

      // iPhone 6 up
      @include media('md', null) {
        margin-top: 2rem;
      }
    }
  }

  .btn-wrapper {
    bottom: 0;
    min-height: 6.2rem;
    position: relative;

    .btn-medium {
      padding: 2rem 5rem;
    }
  }

  .diet-options {
    width: 100%;
  }

  .search-wrapper {
    align-items: center;
    display: flex;
    flex-direction: column;
    padding-bottom: 0.8rem;

    .icon-clear-wrapper {
      position: absolute;
      right: 4rem;
      top: 18rem;

      .icon-clear {
        font-size: 1.3rem;

        &:hover {
          cursor: pointer;
        }
      }
    }

    input {
      margin: 2rem 0;
      text-align: center;

      &::-webkit-input-placeholder {
        line-height: 1.4;
      }

      &::-moz-placeholder {
        line-height: 1.4;
      }

      &:-ms-input-placeholder {
        line-height: 1.4;
      }

      &:-moz-placeholder {
        line-height: 1.4;
      }
    }
  }

  .checkbox-tags {
    width: 100%;
  }

  &.step-one,
  &.step-two {
    .step-heading {
      color: $color-action;
    }
  }

  &.step-one {
    // iPhone 6 up
    @include media('md', null) {
      padding-top: 5rem;
    }

    .btn-wrapper {
      bottom: 1rem;
      position: fixed;
    }
  }

  &.step-two {
    // iPhone 6 up
    @include media('md', null) {
      padding-top: 5rem;
    }

    .step-heading {
      margin-bottom: 0;
    }

    .btn-wrapper {
      // iPhone 6 up
      @include media('md', null) {
        bottom: 1rem;
      }

      // iPhone X
      @include media('md', 'iphone-x') {
        bottom: 3rem;
      }
    }
  }
}
</style>
